import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Box,
  IconButton,
  Typography,
  Tabs,
  Tab,
  Button,
  Stack,
  Chip,
  useMediaQuery,
  useTheme,
  Paper,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Description as DescriptionIcon,
  AttachMoney as AttachMoneyIcon,
  AttachFile as AttachFileIcon,
  Approval as ApprovalIcon,
  Calculate as CalculateIcon,
} from '@mui/icons-material';

// V2 컴포넌트들
import { InfoCard, OrderBookSkeleton, FormErrorSummary, StatusChip } from './CommonComponents';
import OrderBookSummaryV2 from './OrderBookSummaryV2';
import OrderBookInfoV2 from './OrderBookInfoV2';
import OrderBookFormV2 from './OrderBookFormV2';
import SalesProfitFormV2 from './SalesProfitFormV2';
import SalesProfitInfoV2 from './SalesProfitInfoV2';
import ApprovalLineFormV2 from './ApprovalLineFormV2';
import FileUploadFormV2 from './FileUploadFormV2';

// 기존 컨테이너들 (원가산출, 파일, 결재내역)
import OrderProductListContainer from '../../../../containers/projectmng/obtainorder/OrderProductListContainer';
import OrderBookFileListContainer from '../../../../containers/projectmng/obtainorder/OrderBookFileListContainer';
import OrderBookApprovalContainer from '../../../../containers/projectmng/obtainorder/OrderBookApprovalContainer';

/**
 * 개선된 수주품의서 전체 모달 컴포넌트 (V2 Full)
 * 
 * 모드:
 * - add: 신규 작성
 * - edit: 수정
 * - view: 조회
 * 
 * 기능:
 * - 탭 기반 섹션 네비게이션
 * - Sticky 액션바
 * - 반응형 레이아웃
 * - 로딩/저장 상태 표시
 * - 변경사항 추적
 * - 기존 컨테이너 연동
 */
const OrderBookModalV2Full = ({
  mode = 'add', // 'add', 'edit', 'view'
  open,
  handleClose,
  project,
  orderInfo,
  formik,
  user,
  apprUuid,
  setApprUuid,
  approvalList,
  approvalLines,
  approvalLoading,
  salesDetails,
  purchaseDetails,
  scrollRef,
  onDeleteApproval,
  loading = false,
  saving = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState(0);

  // 모드 확인
  const isAddMode = mode === 'add';
  const isEditMode = mode === 'edit';
  const isViewMode = mode === 'view';
  const isEditable = isAddMode || isEditMode;

  // 섹션 refs
  const sectionRefs = {
    summary: useRef(null),
    basic: useRef(null),
    profit: useRef(null),
    files: useRef(null),
    approval: useRef(null),
    cost: useRef(null),
  };

  // 탭 정의
  const getTabs = () => {
    const tabs = [
      { label: '기본정보', icon: <DescriptionIcon fontSize="small" />, key: 'basic' },
      { label: '매출이익', icon: <AttachMoneyIcon fontSize="small" />, key: 'profit' },
      { label: '첨부파일', icon: <AttachFileIcon fontSize="small" />, key: 'files' },
      { label: '결재', icon: <ApprovalIcon fontSize="small" />, key: 'approval' },
      { label: '원가산출', icon: <CalculateIcon fontSize="small" />, key: 'cost' },
    ];
    return tabs;
  };

  const tabs = getTabs();

  // 탭 변경 핸들러
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    const sectionKey = tabs[newValue]?.key;
    if (sectionKey && sectionRefs[sectionKey]?.current) {
      sectionRefs[sectionKey].current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  };

  // 변경사항 여부 확인
  const hasChanges = formik?.dirty || false;

  // 닫기 전 확인
  const handleCloseWithConfirm = () => {
    if (hasChanges && isEditable) {
      if (window.confirm('저장되지 않은 변경사항이 있습니다. 닫으시겠습니까?')) {
        handleClose();
      }
    } else {
      handleClose();
    }
  };

  // 모달 제목
  const getTitle = () => {
    switch (mode) {
      case 'add': return '수주품의서 작성';
      case 'edit': return '수주품의서 수정';
      default: return '수주품의서';
    }
  };

  // 결재선 선택 가능 여부
  const canSelectApproval = isAddMode || 
    (isEditMode && orderInfo?.crtUsr === user?.usrUuid && !approvalList);

  // 로딩 상태
  if (loading) {
    return (
      <Dialog open={open} onClose={handleClose} fullScreen={isMobile} maxWidth="lg" fullWidth>
        <DialogTitle>
          {getTitle()}
          <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <OrderBookSkeleton />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleCloseWithConfirm}
        fullScreen={isMobile}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: isMobile ? '100%' : '90vh',
            maxHeight: isMobile ? '100%' : '90vh',
          }
        }}
      >
        {/* 헤더 */}
        <DialogTitle 
          sx={{ 
            pb: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              {getTitle()}
            </Typography>
            {!isAddMode && orderInfo?.roNo && (
              <Chip 
                label={orderInfo.roNo} 
                size="small" 
                color="primary" 
                variant="outlined"
              />
            )}
            {!isAddMode && orderInfo?.ordYn && (
              <StatusChip status={orderInfo.ordYn} />
            )}
            {hasChanges && isEditable && (
              <Chip 
                icon={<InfoIcon fontSize="small" />}
                label="수정됨" 
                size="small" 
                color="warning" 
                variant="outlined"
              />
            )}
          </Stack>
          <IconButton onClick={handleCloseWithConfirm}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* 탭 네비게이션 */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            variant={isMobile ? 'scrollable' : 'standard'}
            scrollButtons={isMobile ? 'auto' : false}
          >
            {tabs.map((tab) => (
              <Tab 
                key={tab.key}
                icon={tab.icon} 
                iconPosition="start"
                label={tab.label}
                sx={{ minHeight: 48 }}
              />
            ))}
          </Tabs>
        </Box>

        {/* 컨텐츠 */}
        <DialogContent 
          dividers 
          sx={{ 
            p: 3,
            overflowY: 'auto',
          }}
        >
          {/* 에러 요약 */}
          {formik && isEditable && Object.keys(formik.errors).length > 0 && formik.submitCount > 0 && (
            <FormErrorSummary errors={formik.errors} touched={formik.touched} />
          )}

          {/* 스크롤 타겟 */}
          <div ref={scrollRef} />

          {/* 개요 (수정/조회 모드) */}
          {!isAddMode && orderInfo && (
            <Box ref={sectionRefs.summary} sx={{ mb: 3 }}>
              <OrderBookSummaryV2 
                orderInfo={orderInfo} 
                approvalList={approvalList}
                project={project}
              />
            </Box>
          )}

          {/* 기본정보 섹션 */}
          <Box ref={sectionRefs.basic} sx={{ mb: 3 }}>
            {isEditable ? (
              <OrderBookFormV2 
                formik={formik}
                orderInfo={orderInfo}
                project={project}
              />
            ) : (
              <OrderBookInfoV2 
                orderInfo={orderInfo}
                project={project}
                isLink={false}
              />
            )}
          </Box>

          {/* 매출이익 섹션 */}
          <Box ref={sectionRefs.profit} sx={{ mb: 3 }}>
            <InfoCard title="최종 매출이익 (내부거래 포함)" icon={<AttachMoneyIcon />}>
              {isEditable ? (
                <SalesProfitFormV2 formik={formik} project={project} />
              ) : (
                <SalesProfitInfoV2 orderInfo={orderInfo} project={project} />
              )}
            </InfoCard>
          </Box>

          {/* 첨부파일 섹션 */}
          <Box ref={sectionRefs.files} sx={{ mb: 3 }}>
            <InfoCard title="첨부파일" icon={<AttachFileIcon />}>
              {isAddMode ? (
                // 신규: 파일 업로드 폼
                <FileUploadFormV2 formik={formik} />
              ) : (
                // 수정/조회: 기존 파일 목록 + 업로드 폼
                <Box>
                  <OrderBookFileListContainer orderInfo={orderInfo} />
                  {isEditMode && (
                    <Box sx={{ mt: 3, pt: 3, borderTop: '1px dashed', borderColor: 'divider' }}>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                        새 파일 추가
                      </Typography>
                      <FileUploadFormV2 formik={formik} />
                    </Box>
                  )}
                </Box>
              )}
            </InfoCard>
          </Box>

          {/* 결재 섹션 */}
          <Box ref={sectionRefs.approval} sx={{ mb: 3 }}>
            {/* 결재선 선택 (신규 또는 작성자인 경우) */}
            {canSelectApproval && (
              <InfoCard title="결재선 선택" icon={<ApprovalIcon />}>
                <ApprovalLineFormV2
                  approvalLines={approvalLines}
                  apprUuid={apprUuid}
                  setApprUuid={setApprUuid}
                  loading={approvalLoading}
                />
              </InfoCard>
            )}

            {/* 결재 내역 (수정/조회 모드) */}
            {!isAddMode && approvalList && approvalList.length > 0 && (
              <InfoCard title="결재 내역" icon={<ApprovalIcon />}>
                <OrderBookApprovalContainer approvalList={approvalList} />
              </InfoCard>
            )}
          </Box>

          {/* 원가산출 내역 섹션 */}
          <Box ref={sectionRefs.cost}>
            <InfoCard title="원가산출 내역" icon={<CalculateIcon />}>
              <OrderProductListContainer />
            </InfoCard>
          </Box>
        </DialogContent>

        {/* Sticky 액션바 */}
        <DialogActions 
          sx={{ 
            px: 3, 
            py: 2,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Stack 
            direction="row" 
            spacing={2} 
            justifyContent="space-between" 
            alignItems="center"
            sx={{ width: '100%' }}
          >
            {/* 왼쪽: 삭제 버튼 (수정 모드에서만) */}
            <Box>
              {isEditMode && onDeleteApproval && (
                <Button 
                  color="error" 
                  startIcon={<DeleteIcon />}
                  onClick={onDeleteApproval}
                >
                  삭제
                </Button>
              )}
            </Box>

            {/* 오른쪽: 저장/취소 버튼 */}
            <Stack direction="row" spacing={1} alignItems="center">
              {hasChanges && isEditable && (
                <Typography variant="caption" color="warning.main" sx={{ mr: 1 }}>
                  • 저장되지 않은 변경사항
                </Typography>
              )}
              <Button 
                variant="outlined" 
                onClick={handleCloseWithConfirm}
              >
                {isViewMode ? '닫기' : '취소'}
              </Button>
              {isEditable && (
                <Button 
                  variant="contained" 
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                  onClick={formik?.handleSubmit}
                  disabled={saving}
                >
                  {saving ? '저장 중...' : '저장'}
                </Button>
              )}
            </Stack>
          </Stack>
        </DialogActions>
      </Dialog>

      {/* 저장 중 오버레이 */}
      <Backdrop open={saving} sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
        <Paper sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={24} />
          <Typography>저장 중...</Typography>
        </Paper>
      </Backdrop>
    </>
  );
};

export default OrderBookModalV2Full;
